import { MealSelection } from "@/contexts/DietContext";

export type MealKey = keyof MealSelection;

/** Proportional calorie distribution per meal (totals 100%) */
export const mealCaloriePercent: Record<MealKey, number> = {
  breakfast: 0.20,
  morning_snack: 0.05,
  lunch: 0.30,
  snack: 0.15,
  dinner: 0.25,
  optional: 0.05,
};

/** Calculate calorie budget per meal based on total daily calories */
export function getMealCalorieBudgets(totalCalories: number): Record<MealKey, number> {
  const budgets = {} as Record<MealKey, number>;
  for (const key of mealOrder) {
    budgets[key] = Math.round(totalCalories * mealCaloriePercent[key]);
  }
  return budgets;
}

export interface Ingredient {
  name: string;
  amount: string;
  cal: number;
  protein: number;
  carbs: number;
  fat: number;
  taco_food_id?: number;
  quantity_grams?: number;
  source?: string;
}

/**
 * Maps ingredient names to their taco_foods IDs.
 * defaultGrams is used when grams can't be parsed from the amount string.
 */
export const ingredientFoodMap: Record<string, { foodId: number; defaultGrams?: number }> = {
  "Pão francês": { foodId: 53 },
  "Pão integral": { foodId: 5670 },
  "Pão de forma": { foodId: 4499, defaultGrams: 25 },
  "Ovo cozido": { foodId: 488 },
  "Ovo": { foodId: 489 },
  "Banana": { foodId: 182 },
  "Maçã": { foodId: 222 },
  "Laranja": { foodId: 214, defaultGrams: 130 },
  "Melancia": { foodId: 235, defaultGrams: 150 },
  "Mamão papaya": { foodId: 226, defaultGrams: 150 },
  "Melão": { foodId: 236, defaultGrams: 150 },
  "Morango": { foodId: 239 },
  "Abacaxi": { foodId: 164 },
  "Uva": { foodId: 256 },
  "Pêra": { foodId: 243, defaultGrams: 110 },
  "Kiwi": { foodId: 207, defaultGrams: 70 },
  "Caqui": { foodId: 189 },
  "Bergamota (tangerina)": { foodId: 251 },
  "Aveia em flocos": { foodId: 7 },
  "Aveia": { foodId: 7, defaultGrams: 6 },
  "Café coado": { foodId: 471 },
  "Couve": { foodId: 116, defaultGrams: 30 },
  "Couve refogada": { foodId: 116, defaultGrams: 60 },
  "Limão": { foodId: 220, defaultGrams: 15 },
  "Tomate": { foodId: 252, defaultGrams: 30 },
  "Cebola": { foodId: 107, defaultGrams: 15 },
  "Alface": { foodId: 78, defaultGrams: 15 },
  "Azeite": { foodId: 260, defaultGrams: 4 },
  "Peito de frango grelhado": { foodId: 410 },
  "Peito de frango cozido": { foodId: 408 },
  "Peito de frango": { foodId: 410 },
  "Peito de frango desfiado": { foodId: 408 },
  "Peito de frango assado": { foodId: 410 },
  "Queijo branco": { foodId: 461 },
  "Cuscuz de milho": { foodId: 533 },
  "Cuscuz de milho cozido": { foodId: 533 },
  "Arroz branco cozido": { foodId: 3 },
  "Feijão preto cozido": { foodId: 567 },
  "Lentilha cozida": { foodId: 577 },
  "Ervilha cozida": { foodId: 5636 },
  "Batata doce cozida": { foodId: 88 },
  "Batata inglesa cozida": { foodId: 91 },
  "Mandioca cozida": { foodId: 129 },
  "Inhame cozido": { foodId: 126 },
  "Macarrão cozido": { foodId: 5959 },
  "Brócolis cozido": { foodId: 100 },
  "Cenoura": { foodId: 110, defaultGrams: 30 },
  "Pepino": { foodId: 142 },
  "Rúcula": { foodId: 152 },
  "Espinafre": { foodId: 119 },
  "Repolho cru": { foodId: 149 },
  "Milho cozido": { foodId: 4072 },
  "Polenta cozida": { foodId: 62 },
  "Filé de tilápia grelhado": { foodId: 891 },
  "Carne bovina magra grelhada (patinho)": { foodId: 377, defaultGrams: 120 },
  "Carne bovina magra grelhada": { foodId: 377, defaultGrams: 120 },
  "Carne bovina magra cozida (patinho)": { foodId: 377, defaultGrams: 120 },
  "Carne bovina magra cozida": { foodId: 377, defaultGrams: 120 },
  "Carne bovina magra assada (patinho)": { foodId: 377, defaultGrams: 120 },
  "Carne bovina magra": { foodId: 377 },
  "Lombo de porco grelhado": { foodId: 4674, defaultGrams: 120 },
  "Sardinha assada": { foodId: 318 },
  "Salmão grelhado": { foodId: 317 },
  "Peito de peru": { foodId: 1472 },
  "Chocolate ao leite": { foodId: 495, defaultGrams: 25 },
  "Chocolate 70% cacau": { foodId: 498, defaultGrams: 20 },
  "Mel": { foodId: 507, defaultGrams: 7 },
  "Leite condensado": { foodId: 453, defaultGrams: 20 },
  "Doce de leite": { foodId: 501, defaultGrams: 20 },
  "Biscoito de arroz": { foodId: 4816, defaultGrams: 9 },
  "Iogurte natural desnatado": { foodId: 449 },
  "Leite desnatado": { foodId: 3456 },
  "Maionese light": { foodId: 4293, defaultGrams: 5 },
  "Granola": { foodId: 5338 },
  "Goma de tapioca": { foodId: 146 },
  "Chocolate branco": { foodId: 2954, defaultGrams: 25 },
  "Brigadeiro": { foodId: 3325, defaultGrams: 25 },
  "Paçoca": { foodId: 579, defaultGrams: 22 },
  "Pasta de amendoim": { foodId: 4583 },
  "Grão-de-bico cozido": { foodId: 3277, defaultGrams: 80 },
  "Abóbora cozida": { foodId: 64 },
  "Moranga cozida": { foodId: 68 },
};

/** Extract grams from an amount string like "1 un (50g)" → 50 */
export function extractGrams(amount: string, ingredientName?: string): number | undefined {
  const match = amount.match(/\((\d+)g\)/);
  if (match) return parseInt(match[1], 10);
  const gMatch = amount.match(/(\d+)g/);
  if (gMatch) return parseInt(gMatch[1], 10);
  const mlMatch = amount.match(/(\d+)ml/);
  if (mlMatch) return parseInt(mlMatch[1], 10);
  // "Pão de forma" → contar fatias quando a gramagem não estiver explícita (≈25g por fatia)
  if (ingredientName && /pão\s+de\s+forma/i.test(ingredientName)) {
    const slices = amount.match(/(\d+)\s*fatia/i);
    if (slices) return parseInt(slices[1], 10) * 25;
  }
  return undefined;
}

/** Get all unique taco_food_ids from the ingredient map */
export function getAllFoodIds(): number[] {
  return [...new Set(Object.values(ingredientFoodMap).map(v => v.foodId))];
}

export type RecipeCategory = "legume" | "carb" | "protein" | "salad" | "prep";

export interface Recipe {
  id: string;
  label: string;
  emoji: string;
  cal: number;
  protein: number;
  ingredients: Ingredient[];
  category?: RecipeCategory;
}

export interface MealBlock {
  label: string;
  emoji: string;
  items: Recipe[];
}

export const recipes: Record<MealKey, MealBlock> = {
  breakfast: {
    label: "Café da Manhã",
    emoji: "🌅",
    items: [
      {
        id: "pao_ovo", label: "Pão + Ovo", emoji: "🍳", cal: 245, protein: 16,
        ingredients: [
          { name: "Pão de forma", amount: "1 fatia (25g)", cal: 65, protein: 2.4, carbs: 12.2, fat: 0.6 },
          { name: "Ovo cozido", amount: "2 un (100g)", cal: 156, protein: 12, carbs: 1.2, fat: 10.6 },
          { name: "Requeijão light", amount: "15g", cal: 24, protein: 1.5, carbs: 0.5, fat: 2 },
        ],
      },
      {
        id: "pao_frango", label: "Pão + Frango", emoji: "🥪", cal: 250, protein: 20,
        ingredients: [
          { name: "Pão integral", amount: "2 fatias (50g)", cal: 120, protein: 5, carbs: 22, fat: 1.5 },
          { name: "Peito de frango", amount: "80g", cal: 100, protein: 22, carbs: 0, fat: 1.5 },
          { name: "Alface", amount: "2 folhas", cal: 3, protein: 0.3, carbs: 0.5, fat: 0 },
          { name: "Tomate", amount: "2 fatias", cal: 5, protein: 0.2, carbs: 1, fat: 0 },
          { name: "Maionese light", amount: "1 colher chá", cal: 22, protein: 0, carbs: 0.5, fat: 2 },
        ],
      },
      {
        id: "pao_queijo_cafe", label: "Pão + Queijo", emoji: "🧀", cal: 135, protein: 8.4,
        ingredients: [
          { name: "Pão de forma", amount: "1 fatia (25g)", cal: 65, protein: 2.4, carbs: 12.2, fat: 0.6 },
          { name: "Queijo branco", amount: "30g", cal: 70, protein: 6, carbs: 0.5, fat: 5 },
        ],
      },
      {
        id: "sanduiche_natural_cafe", label: "Sanduíche Natural", emoji: "🥪", cal: 310, protein: 26,
        ingredients: [
          { name: "Pão integral", amount: "2 fatias (50g)", cal: 120, protein: 5, carbs: 22, fat: 1.5 },
          { name: "Peito de frango", amount: "60g", cal: 75, protein: 16, carbs: 0, fat: 1 },
          { name: "Queijo branco", amount: "20g", cal: 47, protein: 4, carbs: 0.3, fat: 3.3 },
          { name: "Requeijão light", amount: "15g", cal: 24, protein: 1.5, carbs: 0.5, fat: 2 },
          { name: "Alface", amount: "2 folhas", cal: 3, protein: 0.3, carbs: 0.5, fat: 0 },
          { name: "Tomate", amount: "2 fatias", cal: 5, protein: 0.2, carbs: 1, fat: 0 },
        ],
      },
      {
        id: "tapioca_frango", label: "Tapioca + Frango", emoji: "🫓", cal: 200, protein: 18,
        ingredients: [
          { name: "Goma de tapioca", amount: "3 colheres (30g)", cal: 100, protein: 0.1, carbs: 25, fat: 0 },
          { name: "Peito de frango desfiado", amount: "60g", cal: 75, protein: 16, carbs: 0, fat: 1 },
          { name: "Orégano", amount: "1 pitada", cal: 0, protein: 0, carbs: 0, fat: 0 },
        ],
      },
      {
        id: "tapioca_queijo", label: "Tapioca + Queijo", emoji: "🫓", cal: 170, protein: 6,
        ingredients: [
          { name: "Goma de tapioca", amount: "3 colheres (30g)", cal: 100, protein: 0.1, carbs: 25, fat: 0 },
          { name: "Queijo branco", amount: "30g", cal: 70, protein: 6, carbs: 0.5, fat: 5 },
        ],
      },
      {
        id: "cuscuz_ovo", label: "Cuscuz + Ovo", emoji: "🌽", cal: 262, protein: 14,
        ingredients: [
          { name: "Cuscuz de milho", amount: "50g (cozido)", cal: 106, protein: 2.5, carbs: 22.5, fat: 0.35 },
          { name: "Ovo cozido", amount: "2 un (100g)", cal: 156, protein: 12, carbs: 1.2, fat: 10.6 },
        ],
      },
      {
        id: "cuscuz_queijo", label: "Cuscuz + Queijo", emoji: "🌽", cal: 223, protein: 12,
        ingredients: [
          { name: "Cuscuz de milho", amount: "50g (cozido)", cal: 106, protein: 2.5, carbs: 22.5, fat: 0.35 },
          { name: "Queijo branco", amount: "50g", cal: 117, protein: 10, carbs: 0.8, fat: 8.3 },
        ],
      },
      {
        id: "cuscuz_frango", label: "Cuscuz + Frango", emoji: "🌽", cal: 222, protein: 19,
        ingredients: [
          { name: "Cuscuz de milho", amount: "70g (cozido)", cal: 148, protein: 3.5, carbs: 31.5, fat: 0.45 },
          { name: "Peito de frango desfiado", amount: "60g", cal: 75, protein: 16, carbs: 0, fat: 1 },
        ],
      },
      {
        id: "cuscuz_ovo_queijo", label: "Cuscuz + Ovo e Queijo", emoji: "🌽", cal: 256, protein: 14,
        ingredients: [
          { name: "Cuscuz de milho", amount: "50g (cozido)", cal: 106, protein: 2.5, carbs: 22.5, fat: 0.35 },
          { name: "Ovo cozido", amount: "1 un (50g)", cal: 78, protein: 6, carbs: 0.6, fat: 5.3 },
          { name: "Queijo branco", amount: "30g", cal: 70, protein: 6, carbs: 0.5, fat: 5 },
        ],
      },
      {
        id: "suco_verde_omelete", label: "Suco Verde + Omelete", emoji: "🥬", cal: 225, protein: 13,
        ingredients: [
          { name: "Couve", amount: "1 folha", cal: 5, protein: 0.5, carbs: 0.75, fat: 0.1 },
          { name: "Maçã", amount: "1 un (120g)", cal: 62, protein: 0.4, carbs: 16, fat: 0.2 },
          { name: "Limão", amount: "1/2 un", cal: 8, protein: 0.2, carbs: 3, fat: 0 },
          { name: "Água", amount: "200ml", cal: 0, protein: 0, carbs: 0, fat: 0 },
          { name: "Ovo", amount: "2 un (100g)", cal: 143, protein: 12, carbs: 1.1, fat: 10 },
          { name: "Tomate", amount: "30g", cal: 5, protein: 0.3, carbs: 1, fat: 0 },
          { name: "Cebola", amount: "15g", cal: 6, protein: 0.2, carbs: 1.4, fat: 0 },
        ],
      },
      {
        id: "panqueca_banana_aveia", label: "Panqueca de Banana c/ Aveia", emoji: "🥞", cal: 230, protein: 10,
        ingredients: [
          { name: "Banana", amount: "1 un (100g)", cal: 89, protein: 1.1, carbs: 23, fat: 0.3 },
          { name: "Aveia em flocos", amount: "30g", cal: 105, protein: 3.8, carbs: 19, fat: 2.3 },
          { name: "Ovo", amount: "1 un (50g)", cal: 72, protein: 6, carbs: 0.6, fat: 5 },
          { name: "Canela", amount: "1 pitada", cal: 0, protein: 0, carbs: 0, fat: 0 },
        ],
      },
      {
        id: "fruta_pasta_amendoim", label: "Fruta + Pasta de Amendoim", emoji: "🥜", cal: 180, protein: 5,
        ingredients: [
          { name: "Banana", amount: "1 un (100g)", cal: 89, protein: 1.1, carbs: 23, fat: 0.3 },
          { name: "Pasta de amendoim", amount: "15g", cal: 90, protein: 4, carbs: 3, fat: 7 },
        ],
      },
      {
        id: "iogurte_fruta", label: "Iogurte + Fruta", emoji: "🍓", cal: 150, protein: 8,
        ingredients: [
          { name: "Iogurte natural desnatado", amount: "170g", cal: 85, protein: 8, carbs: 6, fat: 1 },
          { name: "Morango", amount: "5 un (80g)", cal: 26, protein: 0.5, carbs: 6, fat: 0.2 },
          { name: "Mel", amount: "1 colher chá", cal: 20, protein: 0, carbs: 5, fat: 0 },
        ],
      },
      {
        id: "pasta_amendoim_avulsa", label: "Pasta de Amendoim", emoji: "🥜", cal: 95, protein: 4,
        ingredients: [
          { name: "Pasta de amendoim", amount: "15g", cal: 95, protein: 4, carbs: 3, fat: 7.5 },
        ],
      },
      {
        id: "granola_cafe", label: "Granola", emoji: "🥣", cal: 120, protein: 3,
        ingredients: [
          { name: "Granola", amount: "30g", cal: 120, protein: 3, carbs: 20, fat: 3.5 },
        ],
      },
      {
        id: "whey_cafe", label: "Whey Protein", emoji: "💪", cal: 120, protein: 24,
        ingredients: [
          { name: "Whey protein", amount: "1 scoop (30g)", cal: 120, protein: 24, carbs: 2, fat: 1.5 },
        ],
      },
      {
        id: "biscoito_magic_toast", label: "Biscoito Lev Magic Toast", emoji: "🍪", cal: 110, protein: 3,
        ingredients: [
          { name: "Biscoito Lev Magic Toast", amount: "1 pacote (25g)", cal: 110, protein: 3, carbs: 18, fat: 3 },
        ],
      },
      {
        id: "iogurte_desnatado", label: "Iogurte Natural Desnatado", emoji: "🥛", cal: 85, protein: 8,
        ingredients: [
          { name: "Iogurte natural desnatado", amount: "170g", cal: 85, protein: 8, carbs: 6, fat: 1 },
        ],
      },
      {
        id: "iogurte_zero", label: "Iogurte Zero", emoji: "🥛", cal: 60, protein: 7,
        ingredients: [
          { name: "Iogurte zero gordura", amount: "170g", cal: 60, protein: 7, carbs: 6, fat: 0 },
        ],
      },
      {
        id: "iogurte_proteico", label: "Iogurte Proteico", emoji: "🥛", cal: 100, protein: 15,
        ingredients: [
          { name: "Iogurte proteico", amount: "170g", cal: 100, protein: 15, carbs: 6, fat: 0.5 },
        ],
      },
      {
        id: "melancia_cafe", label: "Melancia", emoji: "🍉", cal: 50, protein: 1,
        ingredients: [
          { name: "Melancia", amount: "150g", cal: 50, protein: 0.9, carbs: 12, fat: 0.2 },
        ],
      },
      {
        id: "mamao_cafe", label: "Mamão", emoji: "img:papaya", cal: 60, protein: 1,
        ingredients: [
          { name: "Mamão papaya", amount: "150g", cal: 60, protein: 0.8, carbs: 14, fat: 0.2 },
        ],
      },
      {
        id: "melao_cafe", label: "Melão", emoji: "🍈", cal: 50, protein: 1,
        ingredients: [
          { name: "Melão", amount: "150g", cal: 50, protein: 1.2, carbs: 12, fat: 0.3 },
        ],
      },
      {
        id: "morango_cafe", label: "Morango", emoji: "🍓", cal: 40, protein: 1,
        ingredients: [
          { name: "Morango", amount: "100g", cal: 40, protein: 0.7, carbs: 8, fat: 0.3 },
        ],
      },
      {
        id: "banana_cafe", label: "Banana", emoji: "🍌", cal: 89, protein: 1,
        ingredients: [
          { name: "Banana", amount: "1 un (100g)", cal: 89, protein: 1.1, carbs: 23, fat: 0.3 },
        ],
      },
      {
        id: "maca_cafe", label: "Maçã", emoji: "🍎", cal: 52, protein: 0,
        ingredients: [
          { name: "Maçã", amount: "1 un (100g)", cal: 52, protein: 0.3, carbs: 14, fat: 0.2 },
        ],
      },
      {
        id: "laranja_cafe", label: "Laranja", emoji: "🍊", cal: 47, protein: 1,
        ingredients: [
          { name: "Laranja", amount: "1 un (130g)", cal: 47, protein: 0.9, carbs: 12, fat: 0.1 },
        ],
      },
      {
        id: "cafe_puro", label: "Café", emoji: "☕", cal: 5, protein: 0,
        ingredients: [
          { name: "Café coado", amount: "1 xícara (240ml)", cal: 5, protein: 0.3, carbs: 0, fat: 0 },
        ],
      },
      {
        id: "cafe_leite_desnatado", label: "Café com Leite Desnatado", emoji: "☕", cal: 45, protein: 4,
        ingredients: [
          { name: "Café coado", amount: "1 xícara (140ml)", cal: 3, protein: 0.2, carbs: 0, fat: 0 },
          { name: "Leite desnatado", amount: "100ml", cal: 42, protein: 3.5, carbs: 5, fat: 0.5 },
        ],
      },
      {
        id: "leite_desnatado_cafe", label: "Leite Desnatado", emoji: "🥛", cal: 70, protein: 7,
        ingredients: [
          { name: "Leite desnatado", amount: "200ml", cal: 70, protein: 7, carbs: 10, fat: 0.5 },
        ],
      },
      {
        id: "suco_verde_cafe", label: "Suco Verde", emoji: "🥬", cal: 75, protein: 1,
        ingredients: [
          { name: "Couve", amount: "1 folha", cal: 5, protein: 0.5, carbs: 0.75, fat: 0.1 },
          { name: "Maçã", amount: "1 un (120g)", cal: 62, protein: 0.4, carbs: 16, fat: 0.2 },
          { name: "Limão", amount: "1/2 un", cal: 8, protein: 0.2, carbs: 3, fat: 0 },
          { name: "Água", amount: "200ml", cal: 0, protein: 0, carbs: 0, fat: 0 },
        ],
      },
      {
        id: "omelete", label: "Omelete", emoji: "🥚", cal: 180, protein: 14,
        ingredients: [
          { name: "Ovo", amount: "2 un (100g)", cal: 143, protein: 12, carbs: 1.1, fat: 10 },
          { name: "Tomate", amount: "30g", cal: 5, protein: 0.3, carbs: 1, fat: 0 },
          { name: "Cebola", amount: "15g", cal: 6, protein: 0.2, carbs: 1.4, fat: 0 },
          { name: "Sal", amount: "1 pitada", cal: 0, protein: 0, carbs: 0, fat: 0 },
        ],
      },
      {
        id: "aveia_banana", label: "Aveia + Banana", emoji: "🥣", cal: 159, protein: 3.6,
        ingredients: [
          { name: "Aveia em flocos", amount: "20g", cal: 70, protein: 2.5, carbs: 12.5, fat: 1.5 },
          { name: "Banana", amount: "1 un (100g)", cal: 89, protein: 1.1, carbs: 23, fat: 0.3 },
          { name: "Canela", amount: "1 pitada", cal: 0, protein: 0, carbs: 0, fat: 0 },
        ],
      },
      {
        id: "vitamina", label: "Vitamina de Frutas", emoji: "🥤", cal: 180, protein: 8,
        ingredients: [
          { name: "Leite desnatado", amount: "200ml", cal: 70, protein: 7, carbs: 10, fat: 0.5 },
          { name: "Banana", amount: "1 un (100g)", cal: 89, protein: 1.1, carbs: 23, fat: 0.3 },
          { name: "Aveia", amount: "1 colher", cal: 21, protein: 0.7, carbs: 3.5, fat: 0.4 },
        ],
      },
    ],
  },
  morning_snack: {
    label: "Lanche da Manhã",
    emoji: "🍎",
    items: [
      {
        id: "banana_lm", label: "Banana", emoji: "🍌", cal: 89, protein: 1,
        ingredients: [{ name: "Banana", amount: "1 un (100g)", cal: 89, protein: 1.1, carbs: 23, fat: 0.3 }],
      },
      {
        id: "maca_lm", label: "Maçã", emoji: "🍎", cal: 52, protein: 0,
        ingredients: [{ name: "Maçã", amount: "1 un (100g)", cal: 52, protein: 0.3, carbs: 14, fat: 0.2 }],
      },
      {
        id: "melao_lm", label: "Melão", emoji: "🍈", cal: 50, protein: 1,
        ingredients: [{ name: "Melão", amount: "150g", cal: 50, protein: 1.2, carbs: 12, fat: 0.3 }],
      },
      {
        id: "mamao_lm", label: "Mamão", emoji: "img:papaya", cal: 60, protein: 1,
        ingredients: [{ name: "Mamão papaya", amount: "150g", cal: 60, protein: 0.8, carbs: 14, fat: 0.2 }],
      },
      {
        id: "melancia_lm", label: "Melancia", emoji: "🍉", cal: 50, protein: 1,
        ingredients: [{ name: "Melancia", amount: "150g", cal: 50, protein: 0.9, carbs: 12, fat: 0.2 }],
      },
      {
        id: "abacaxi_lm", label: "Abacaxi", emoji: "🍍", cal: 50, protein: 1,
        ingredients: [{ name: "Abacaxi", amount: "100g", cal: 50, protein: 0.5, carbs: 13, fat: 0.1 }],
      },
      {
        id: "uva_lm", label: "Uva", emoji: "🍇", cal: 69, protein: 1,
        ingredients: [{ name: "Uva", amount: "100g", cal: 69, protein: 0.7, carbs: 18, fat: 0.2 }],
      },
      {
        id: "morango_lm", label: "Morango", emoji: "🍓", cal: 40, protein: 1,
        ingredients: [{ name: "Morango", amount: "100g", cal: 40, protein: 0.7, carbs: 8, fat: 0.3 }],
      },
      {
        id: "laranja_lm", label: "Laranja", emoji: "🍊", cal: 47, protein: 1,
        ingredients: [{ name: "Laranja", amount: "1 un (130g)", cal: 47, protein: 0.9, carbs: 12, fat: 0.1 }],
      },
      {
        id: "pera_lm", label: "Pêra", emoji: "🍐", cal: 53, protein: 0,
        ingredients: [{ name: "Pêra", amount: "1 un (110g)", cal: 53, protein: 0.4, carbs: 14, fat: 0.1 }],
      },
      {
        id: "kiwi_lm", label: "Kiwi", emoji: "🥝", cal: 42, protein: 1,
        ingredients: [{ name: "Kiwi", amount: "1 un (70g)", cal: 42, protein: 0.8, carbs: 10, fat: 0.4 }],
      },
      {
        id: "caqui_lm", label: "Caqui", emoji: "🍅", cal: 71, protein: 0,
        ingredients: [{ name: "Caqui", amount: "1 un (100g)", cal: 71, protein: 0.4, carbs: 19, fat: 0.2 }],
      },
      {
        id: "bergamota_lm", label: "Bergamota", emoji: "🍊", cal: 44, protein: 1,
        ingredients: [{ name: "Bergamota (tangerina)", amount: "1 un (100g)", cal: 44, protein: 0.7, carbs: 11, fat: 0.2 }],
      },
      {
        id: "biscoito_arroz_lm", label: "Biscoito de Arroz", emoji: "🍘", cal: 35, protein: 1,
        ingredients: [{ name: "Biscoito de arroz", amount: "3 un (9g)", cal: 35, protein: 0.7, carbs: 7, fat: 0.3 }],
      },
      {
        id: "pasta_amendoim_lm", label: "Pasta de Amendoim", emoji: "🥜", cal: 95, protein: 4,
        ingredients: [{ name: "Pasta de amendoim", amount: "15g", cal: 95, protein: 4, carbs: 3, fat: 7.5 }],
      },
      {
        id: "ovo_lm", label: "Ovo", emoji: "🥚", cal: 78, protein: 6,
        ingredients: [{ name: "Ovo cozido", amount: "1 un (50g)", cal: 78, protein: 6, carbs: 0.6, fat: 5.3 }],
      },
      {
        id: "iogurte_natural_lm", label: "Iogurte Natural", emoji: "🥛", cal: 85, protein: 8,
        ingredients: [{ name: "Iogurte natural desnatado", amount: "170g", cal: 85, protein: 8, carbs: 6, fat: 1 }],
      },
      {
        id: "iogurte_zero_lm", label: "Iogurte Zero", emoji: "🥛", cal: 60, protein: 7,
        ingredients: [{ name: "Iogurte zero gordura", amount: "170g", cal: 60, protein: 7, carbs: 6, fat: 0 }],
      },
      {
        id: "iogurte_proteico_lm", label: "Iogurte Proteico", emoji: "🥛", cal: 100, protein: 15,
        ingredients: [{ name: "Iogurte proteico", amount: "170g", cal: 100, protein: 15, carbs: 6, fat: 0.5 }],
      },
      {
        id: "biscoito_magic_toast_lm", label: "Biscoito Lev Magic Toast", emoji: "🍪", cal: 110, protein: 3,
        ingredients: [{ name: "Biscoito Lev Magic Toast", amount: "1 pacote (25g)", cal: 110, protein: 3, carbs: 18, fat: 3 }],
      },
      {
        id: "whey_lm", label: "Whey Protein", emoji: "💪", cal: 120, protein: 24,
        ingredients: [{ name: "Whey protein", amount: "1 scoop (30g)", cal: 120, protein: 24, carbs: 2, fat: 1.5 }],
      },
      {
        id: "leite_desnatado_lm", label: "Leite Desnatado", emoji: "🥛", cal: 70, protein: 7,
        ingredients: [{ name: "Leite desnatado", amount: "200ml", cal: 70, protein: 7, carbs: 10, fat: 0.5 }],
      },
    ],
  },
  lunch: {
    label: "Almoço",
    emoji: "🍽️",
    items: [
      // === Leguminosas ===
      { id: "feijao_preto", label: "Feijão Preto", emoji: "🫘", cal: 77, protein: 5, category: "legume", ingredients: [{ name: "Feijão preto cozido", amount: "1 concha (80g)", cal: 77, protein: 5, carbs: 14, fat: 0.5 }] },
      { id: "lentilha", label: "Lentilha", emoji: "🫘", cal: 93, protein: 7, category: "legume", ingredients: [{ name: "Lentilha cozida", amount: "80g", cal: 93, protein: 7, carbs: 16, fat: 0.3 }] },
      { id: "ervilha", label: "Ervilha", emoji: "🫛", cal: 63, protein: 4, category: "legume", ingredients: [{ name: "Ervilha cozida", amount: "80g", cal: 63, protein: 4, carbs: 11, fat: 0.3 }] },
      { id: "grao_de_bico", label: "Grão-de-Bico", emoji: "🫘", cal: 130, protein: 7, category: "legume", ingredients: [{ name: "Grão-de-bico cozido", amount: "80g", cal: 130, protein: 7, carbs: 22, fat: 2 }] },
      // === Carboidratos ===
      { id: "arroz_almoco", label: "Arroz", emoji: "🍚", cal: 130, protein: 3, category: "carb", ingredients: [{ name: "Arroz branco cozido", amount: "4 colheres (100g)", cal: 130, protein: 2.5, carbs: 28, fat: 0.3 }] },
      { id: "arroz_feijao_almoco", label: "Feijão + Arroz", emoji: "🍚", cal: 207, protein: 8, category: "carb", ingredients: [{ name: "Arroz branco cozido", amount: "4 colheres (100g)", cal: 130, protein: 2.5, carbs: 28, fat: 0.3 }, { name: "Feijão preto cozido", amount: "1 concha (80g)", cal: 77, protein: 5, carbs: 14, fat: 0.5 }] },
      { id: "cuscuz_almoco", label: "Cuscuz", emoji: "🌽", cal: 170, protein: 4, category: "carb", ingredients: [{ name: "Cuscuz de milho cozido", amount: "80g", cal: 170, protein: 4, carbs: 36, fat: 0.5 }] },
      { id: "tapioca_almoco", label: "Tapioca", emoji: "🫓", cal: 100, protein: 0, category: "carb", ingredients: [{ name: "Goma de tapioca", amount: "3 colheres (30g)", cal: 100, protein: 0.1, carbs: 25, fat: 0 }] },
      { id: "polenta_almoco", label: "Polenta Cozida", emoji: "🟡", cal: 70, protein: 2, category: "carb", ingredients: [{ name: "Polenta cozida", amount: "100g", cal: 70, protein: 1.5, carbs: 16, fat: 0.3 }] },
      { id: "macarrao_almoco", label: "Macarrão", emoji: "🍝", cal: 160, protein: 5, category: "carb", ingredients: [{ name: "Macarrão cozido", amount: "100g", cal: 160, protein: 5, carbs: 32, fat: 0.8 }] },
      { id: "batata_doce_almoco", label: "Batata Doce", emoji: "🍠", cal: 90, protein: 1, category: "carb", ingredients: [{ name: "Batata doce cozida", amount: "100g", cal: 90, protein: 1.3, carbs: 21, fat: 0.1 }] },
      { id: "batata_inglesa", label: "Batata Inglesa", emoji: "🥔", cal: 52, protein: 1, category: "carb", ingredients: [{ name: "Batata inglesa cozida", amount: "100g", cal: 52, protein: 1.2, carbs: 12, fat: 0.1 }] },
      { id: "mandioca_almoco", label: "Mandioca", emoji: "🥔", cal: 125, protein: 1, category: "carb", ingredients: [{ name: "Mandioca cozida", amount: "100g", cal: 125, protein: 0.6, carbs: 30, fat: 0.3 }] },
      { id: "inhame_almoco", label: "Inhame", emoji: "🥔", cal: 97, protein: 2, category: "carb", ingredients: [{ name: "Inhame cozido", amount: "100g", cal: 97, protein: 2, carbs: 23, fat: 0.1 }] },
      { id: "abobora_almoco", label: "Abóbora", emoji: "🎃", cal: 30, protein: 1, category: "carb", ingredients: [{ name: "Abóbora cozida", amount: "100g", cal: 30, protein: 0.8, carbs: 7, fat: 0.1 }] },
      { id: "moranga_almoco", label: "Moranga", emoji: "🎃", cal: 28, protein: 1, category: "carb", ingredients: [{ name: "Moranga cozida", amount: "100g", cal: 28, protein: 0.8, carbs: 6, fat: 0.1 }] },
      { id: "milho_almoco", label: "Milho", emoji: "🌽", cal: 96, protein: 3, category: "carb", ingredients: [{ name: "Milho cozido", amount: "1 espiga (100g)", cal: 96, protein: 3.2, carbs: 19, fat: 1.2 }] },
      // === Proteínas ===
      { id: "frango_grelhado", label: "Frango Grelhado", emoji: "🍗", cal: 165, protein: 31, category: "protein", ingredients: [{ name: "Peito de frango grelhado", amount: "150g", cal: 165, protein: 31, carbs: 0, fat: 3.6 }] },
      { id: "frango_cozido", label: "Frango Cozido", emoji: "🍗", cal: 150, protein: 28, category: "protein", ingredients: [{ name: "Peito de frango cozido", amount: "150g", cal: 150, protein: 28, carbs: 0, fat: 3.2 }] },
      { id: "frango_assado", label: "Frango Assado", emoji: "🍗", cal: 180, protein: 30, category: "protein", ingredients: [{ name: "Peito de frango assado", amount: "150g", cal: 180, protein: 30, carbs: 0, fat: 6 }] },
      { id: "carne_grelhada", label: "Carne Magra Grelhada", emoji: "🥩", cal: 180, protein: 32, category: "protein", ingredients: [{ name: "Carne bovina magra grelhada (patinho)", amount: "120g", cal: 180, protein: 32, carbs: 0, fat: 5.5 }] },
      { id: "carne_cozida", label: "Carne Magra Cozida", emoji: "🥩", cal: 170, protein: 30, category: "protein", ingredients: [{ name: "Carne bovina magra cozida (patinho)", amount: "120g", cal: 170, protein: 30, carbs: 0, fat: 5 }] },
      { id: "carne_assada", label: "Carne Magra Assada", emoji: "🥩", cal: 190, protein: 33, category: "protein", ingredients: [{ name: "Carne bovina magra assada (patinho)", amount: "120g", cal: 190, protein: 33, carbs: 0, fat: 6.5 }] },
      { id: "peixe_almoco", label: "Peixe", emoji: "🐟", cal: 155, protein: 33, category: "protein", ingredients: [{ name: "Filé de tilápia grelhado", amount: "150g", cal: 155, protein: 33, carbs: 0, fat: 2.3 }] },
      { id: "carne_moida", label: "Carne Moída Magra", emoji: "🥩", cal: 165, protein: 26, category: "protein", ingredients: [{ name: "Carne moída magra refogada", amount: "100g", cal: 165, protein: 26, carbs: 0, fat: 6.5 }] },
      { id: "carne_porco", label: "Carne de Porco", emoji: "🥩", cal: 190, protein: 28, category: "protein", ingredients: [{ name: "Lombo de porco grelhado", amount: "120g", cal: 190, protein: 28, carbs: 0, fat: 8 }] },
      // === Saladas e Verduras ===
      { id: "salada_completa_almoco", label: "Salada de Verduras e Legumes", emoji: "🥗", cal: 64, protein: 1, category: "salad", ingredients: [
        { name: "Alface", amount: "3 folhas (25g)", cal: 2, protein: 0.2, carbs: 0.3, fat: 0 },
        { name: "Tomate", amount: "2 fatias (45g)", cal: 16, protein: 0.3, carbs: 3.5, fat: 0.1 },
        { name: "Pepino", amount: "45g", cal: 5, protein: 0.3, carbs: 1.7, fat: 0.1 },
        { name: "Cenoura", amount: "25g", cal: 8, protein: 0.3, carbs: 2, fat: 0.1 },
        { name: "Azeite", amount: "1 colher chá (5g)", cal: 33, protein: 0, carbs: 0, fat: 3.7 },
      ] },
      { id: "alface_almoco", label: "Alface", emoji: "🥬", cal: 5, protein: 0, category: "salad", ingredients: [{ name: "Alface", amount: "3 folhas (30g)", cal: 5, protein: 0.5, carbs: 1, fat: 0 }] },
      { id: "tomate_almoco", label: "Tomate", emoji: "🍅", cal: 18, protein: 1, category: "salad", ingredients: [{ name: "Tomate", amount: "1 un (100g)", cal: 18, protein: 0.9, carbs: 4, fat: 0.2 }] },
      { id: "pepino_almoco", label: "Pepino", emoji: "🥒", cal: 10, protein: 0, category: "salad", ingredients: [{ name: "Pepino", amount: "80g", cal: 10, protein: 0.4, carbs: 2, fat: 0 }] },
      { id: "repolho_almoco", label: "Repolho", emoji: "🥬", cal: 17, protein: 1, category: "salad", ingredients: [{ name: "Repolho cru", amount: "80g", cal: 17, protein: 1, carbs: 4, fat: 0.1 }] },
      { id: "brocolis_almoco", label: "Brócolis", emoji: "🥦", cal: 27, protein: 2, category: "salad", ingredients: [{ name: "Brócolis cozido", amount: "80g", cal: 27, protein: 2.3, carbs: 5, fat: 0.3 }] },
      { id: "couve_almoco", label: "Couve Folha", emoji: "🥬", cal: 20, protein: 2, category: "salad", ingredients: [{ name: "Couve refogada", amount: "60g", cal: 20, protein: 1.5, carbs: 3, fat: 0.5 }] },
    ],
  },
  snack: {
    label: "Lanche da Tarde",
    emoji: "🍪",
    items: [
      {
        id: "pao_ovo_tarde", label: "Pão + Ovo", emoji: "🍳", cal: 245, protein: 16,
        ingredients: [
          { name: "Pão de forma", amount: "1 fatia (25g)", cal: 65, protein: 2.4, carbs: 12.2, fat: 0.6 },
          { name: "Ovo cozido", amount: "2 un (100g)", cal: 156, protein: 12, carbs: 1.2, fat: 10.6 },
          { name: "Requeijão light", amount: "15g", cal: 24, protein: 1.5, carbs: 0.5, fat: 2 },
        ],
      },
      {
        id: "pao_frango_tarde", label: "Pão + Frango", emoji: "🥪", cal: 250, protein: 20,
        ingredients: [
          { name: "Pão integral", amount: "2 fatias (50g)", cal: 120, protein: 5, carbs: 22, fat: 1.5 },
          { name: "Peito de frango", amount: "80g", cal: 100, protein: 22, carbs: 0, fat: 1.5 },
          { name: "Alface", amount: "2 folhas", cal: 3, protein: 0.3, carbs: 0.5, fat: 0 },
          { name: "Tomate", amount: "2 fatias", cal: 5, protein: 0.2, carbs: 1, fat: 0 },
          { name: "Maionese light", amount: "1 colher chá", cal: 22, protein: 0, carbs: 0.5, fat: 2 },
        ],
      },
      {
        id: "pao_queijo_tarde", label: "Pão + Queijo", emoji: "🧀", cal: 135, protein: 8.4,
        ingredients: [
          { name: "Pão de forma", amount: "1 fatia (25g)", cal: 65, protein: 2.4, carbs: 12.2, fat: 0.6 },
          { name: "Queijo branco", amount: "30g", cal: 70, protein: 6, carbs: 0.5, fat: 5 },
        ],
      },
      {
        id: "sanduiche_natural_tarde", label: "Sanduíche Natural", emoji: "🥪", cal: 310, protein: 26,
        ingredients: [
          { name: "Pão integral", amount: "2 fatias (50g)", cal: 120, protein: 5, carbs: 22, fat: 1.5 },
          { name: "Peito de frango", amount: "60g", cal: 75, protein: 16, carbs: 0, fat: 1 },
          { name: "Queijo branco", amount: "20g", cal: 47, protein: 4, carbs: 0.3, fat: 3.3 },
          { name: "Requeijão light", amount: "15g", cal: 24, protein: 1.5, carbs: 0.5, fat: 2 },
          { name: "Alface", amount: "2 folhas", cal: 3, protein: 0.3, carbs: 0.5, fat: 0 },
          { name: "Tomate", amount: "2 fatias", cal: 5, protein: 0.2, carbs: 1, fat: 0 },
        ],
      },
      {
        id: "tapioca_frango_tarde", label: "Tapioca + Frango", emoji: "🫓", cal: 200, protein: 18,
        ingredients: [
          { name: "Goma de tapioca", amount: "3 colheres (30g)", cal: 100, protein: 0.1, carbs: 25, fat: 0 },
          { name: "Peito de frango desfiado", amount: "60g", cal: 75, protein: 16, carbs: 0, fat: 1 },
          { name: "Orégano", amount: "1 pitada", cal: 0, protein: 0, carbs: 0, fat: 0 },
        ],
      },
      {
        id: "tapioca_queijo_tarde", label: "Tapioca + Queijo", emoji: "🫓", cal: 170, protein: 6,
        ingredients: [
          { name: "Goma de tapioca", amount: "3 colheres (30g)", cal: 100, protein: 0.1, carbs: 25, fat: 0 },
          { name: "Queijo branco", amount: "30g", cal: 70, protein: 6, carbs: 0.5, fat: 5 },
        ],
      },
      {
        id: "cuscuz_ovo_tarde", label: "Cuscuz + Ovo", emoji: "🌽", cal: 262, protein: 14,
        ingredients: [
          { name: "Cuscuz de milho", amount: "50g (cozido)", cal: 106, protein: 2.5, carbs: 22.5, fat: 0.35 },
          { name: "Ovo cozido", amount: "2 un (100g)", cal: 156, protein: 12, carbs: 1.2, fat: 10.6 },
        ],
      },
      {
        id: "cuscuz_queijo_tarde", label: "Cuscuz + Queijo", emoji: "🌽", cal: 223, protein: 12,
        ingredients: [
          { name: "Cuscuz de milho", amount: "50g (cozido)", cal: 106, protein: 2.5, carbs: 22.5, fat: 0.35 },
          { name: "Queijo branco", amount: "50g", cal: 117, protein: 10, carbs: 0.8, fat: 8.3 },
        ],
      },
      {
        id: "cuscuz_frango_tarde", label: "Cuscuz + Frango", emoji: "🌽", cal: 222, protein: 19,
        ingredients: [
          { name: "Cuscuz de milho", amount: "70g (cozido)", cal: 148, protein: 3.5, carbs: 31.5, fat: 0.45 },
          { name: "Peito de frango desfiado", amount: "60g", cal: 75, protein: 16, carbs: 0, fat: 1 },
        ],
      },
      {
        id: "cuscuz_ovo_queijo_tarde", label: "Cuscuz + Ovo e Queijo", emoji: "🌽", cal: 256, protein: 14,
        ingredients: [
          { name: "Cuscuz de milho", amount: "50g (cozido)", cal: 106, protein: 2.5, carbs: 22.5, fat: 0.35 },
          { name: "Ovo cozido", amount: "1 un (50g)", cal: 78, protein: 6, carbs: 0.6, fat: 5.3 },
          { name: "Queijo branco", amount: "30g", cal: 70, protein: 6, carbs: 0.5, fat: 5 },
        ],
      },
      {
        id: "suco_verde_omelete_tarde", label: "Suco Verde + Omelete", emoji: "🥬", cal: 225, protein: 13,
        ingredients: [
          { name: "Couve", amount: "1 folha", cal: 5, protein: 0.5, carbs: 0.75, fat: 0.1 },
          { name: "Maçã", amount: "1 un (120g)", cal: 62, protein: 0.4, carbs: 16, fat: 0.2 },
          { name: "Limão", amount: "1/2 un", cal: 8, protein: 0.2, carbs: 3, fat: 0 },
          { name: "Água", amount: "200ml", cal: 0, protein: 0, carbs: 0, fat: 0 },
          { name: "Ovo", amount: "2 un (100g)", cal: 143, protein: 12, carbs: 1.1, fat: 10 },
          { name: "Tomate", amount: "30g", cal: 5, protein: 0.3, carbs: 1, fat: 0 },
          { name: "Cebola", amount: "15g", cal: 6, protein: 0.2, carbs: 1.4, fat: 0 },
        ],
      },
      {
        id: "panqueca_banana_aveia_tarde", label: "Panqueca de Banana c/ Aveia", emoji: "🥞", cal: 230, protein: 10,
        ingredients: [
          { name: "Banana", amount: "1 un (100g)", cal: 89, protein: 1.1, carbs: 23, fat: 0.3 },
          { name: "Aveia em flocos", amount: "30g", cal: 105, protein: 3.8, carbs: 19, fat: 2.3 },
          { name: "Ovo", amount: "1 un (50g)", cal: 72, protein: 6, carbs: 0.6, fat: 5 },
          { name: "Canela", amount: "1 pitada", cal: 0, protein: 0, carbs: 0, fat: 0 },
        ],
      },
      {
        id: "fruta_pasta_amendoim_tarde", label: "Fruta + Pasta de Amendoim", emoji: "🥜", cal: 180, protein: 5,
        ingredients: [
          { name: "Banana", amount: "1 un (100g)", cal: 89, protein: 1.1, carbs: 23, fat: 0.3 },
          { name: "Pasta de amendoim", amount: "15g", cal: 90, protein: 4, carbs: 3, fat: 7 },
        ],
      },
      {
        id: "iogurte_fruta_tarde", label: "Iogurte + Fruta", emoji: "🍓", cal: 150, protein: 8,
        ingredients: [
          { name: "Iogurte natural desnatado", amount: "170g", cal: 85, protein: 8, carbs: 6, fat: 1 },
          { name: "Morango", amount: "5 un (80g)", cal: 26, protein: 0.5, carbs: 6, fat: 0.2 },
          { name: "Mel", amount: "1 colher chá", cal: 20, protein: 0, carbs: 5, fat: 0 },
        ],
      },
      {
        id: "pasta_amendoim_tarde", label: "Pasta de Amendoim", emoji: "🥜", cal: 95, protein: 4,
        ingredients: [{ name: "Pasta de amendoim", amount: "15g", cal: 95, protein: 4, carbs: 3, fat: 7.5 }],
      },
      {
        id: "granola_tarde", label: "Granola", emoji: "🥣", cal: 120, protein: 3,
        ingredients: [{ name: "Granola", amount: "30g", cal: 120, protein: 3, carbs: 20, fat: 3.5 }],
      },
      {
        id: "whey_tarde", label: "Whey Protein", emoji: "💪", cal: 120, protein: 24,
        ingredients: [{ name: "Whey protein", amount: "1 scoop (30g)", cal: 120, protein: 24, carbs: 2, fat: 1.5 }],
      },
      {
        id: "biscoito_magic_toast_tarde", label: "Biscoito Lev Magic Toast", emoji: "🍪", cal: 110, protein: 3,
        ingredients: [{ name: "Biscoito Lev Magic Toast", amount: "1 pacote (25g)", cal: 110, protein: 3, carbs: 18, fat: 3 }],
      },
      {
        id: "iogurte_desnatado_tarde", label: "Iogurte Natural Desnatado", emoji: "🥛", cal: 85, protein: 8,
        ingredients: [{ name: "Iogurte natural desnatado", amount: "170g", cal: 85, protein: 8, carbs: 6, fat: 1 }],
      },
      {
        id: "iogurte_zero_tarde", label: "Iogurte Zero", emoji: "🥛", cal: 60, protein: 7,
        ingredients: [{ name: "Iogurte zero gordura", amount: "170g", cal: 60, protein: 7, carbs: 6, fat: 0 }],
      },
      {
        id: "iogurte_proteico_tarde", label: "Iogurte Proteico", emoji: "🥛", cal: 100, protein: 15,
        ingredients: [{ name: "Iogurte proteico", amount: "170g", cal: 100, protein: 15, carbs: 6, fat: 0.5 }],
      },
      {
        id: "melancia_tarde", label: "Melancia", emoji: "🍉", cal: 50, protein: 1,
        ingredients: [{ name: "Melancia", amount: "150g", cal: 50, protein: 0.9, carbs: 12, fat: 0.2 }],
      },
      {
        id: "mamao_tarde", label: "Mamão", emoji: "img:papaya", cal: 60, protein: 1,
        ingredients: [{ name: "Mamão papaya", amount: "150g", cal: 60, protein: 0.8, carbs: 14, fat: 0.2 }],
      },
      {
        id: "melao_tarde", label: "Melão", emoji: "🍈", cal: 50, protein: 1,
        ingredients: [{ name: "Melão", amount: "150g", cal: 50, protein: 1.2, carbs: 12, fat: 0.3 }],
      },
      {
        id: "morango_tarde", label: "Morango", emoji: "🍓", cal: 40, protein: 1,
        ingredients: [{ name: "Morango", amount: "100g", cal: 40, protein: 0.7, carbs: 8, fat: 0.3 }],
      },
      {
        id: "banana_tarde", label: "Banana", emoji: "🍌", cal: 89, protein: 1,
        ingredients: [{ name: "Banana", amount: "1 un (100g)", cal: 89, protein: 1.1, carbs: 23, fat: 0.3 }],
      },
      {
        id: "maca_tarde", label: "Maçã", emoji: "🍎", cal: 52, protein: 0,
        ingredients: [{ name: "Maçã", amount: "1 un (100g)", cal: 52, protein: 0.3, carbs: 14, fat: 0.2 }],
      },
      {
        id: "laranja_tarde", label: "Laranja", emoji: "🍊", cal: 47, protein: 1,
        ingredients: [{ name: "Laranja", amount: "1 un (130g)", cal: 47, protein: 0.9, carbs: 12, fat: 0.1 }],
      },
      {
        id: "cafe_puro_tarde", label: "Café", emoji: "☕", cal: 5, protein: 0,
        ingredients: [{ name: "Café coado", amount: "1 xícara (240ml)", cal: 5, protein: 0.3, carbs: 0, fat: 0 }],
      },
      {
        id: "cafe_leite_desnatado_tarde", label: "Café com Leite Desnatado", emoji: "☕", cal: 45, protein: 4,
        ingredients: [
          { name: "Café coado", amount: "1 xícara (140ml)", cal: 3, protein: 0.2, carbs: 0, fat: 0 },
          { name: "Leite desnatado", amount: "100ml", cal: 42, protein: 3.5, carbs: 5, fat: 0.5 },
        ],
      },
      {
        id: "leite_desnatado_tarde", label: "Leite Desnatado", emoji: "🥛", cal: 70, protein: 7,
        ingredients: [{ name: "Leite desnatado", amount: "200ml", cal: 70, protein: 7, carbs: 10, fat: 0.5 }],
      },
      {
        id: "suco_verde_tarde", label: "Suco Verde", emoji: "🥬", cal: 75, protein: 1,
        ingredients: [
          { name: "Couve", amount: "1 folha", cal: 5, protein: 0.5, carbs: 0.75, fat: 0.1 },
          { name: "Maçã", amount: "1 un (120g)", cal: 62, protein: 0.4, carbs: 16, fat: 0.2 },
          { name: "Limão", amount: "1/2 un", cal: 8, protein: 0.2, carbs: 3, fat: 0 },
          { name: "Água", amount: "200ml", cal: 0, protein: 0, carbs: 0, fat: 0 },
        ],
      },
      {
        id: "omelete_tarde", label: "Omelete", emoji: "🥚", cal: 180, protein: 14,
        ingredients: [
          { name: "Ovo", amount: "2 un (100g)", cal: 143, protein: 12, carbs: 1.1, fat: 10 },
          { name: "Tomate", amount: "30g", cal: 5, protein: 0.3, carbs: 1, fat: 0 },
          { name: "Cebola", amount: "15g", cal: 6, protein: 0.2, carbs: 1.4, fat: 0 },
          { name: "Sal", amount: "1 pitada", cal: 0, protein: 0, carbs: 0, fat: 0 },
        ],
      },
      {
        id: "aveia_banana_tarde", label: "Aveia + Banana", emoji: "🥣", cal: 159, protein: 3.6,
        ingredients: [
          { name: "Aveia em flocos", amount: "20g", cal: 70, protein: 2.5, carbs: 12.5, fat: 1.5 },
          { name: "Banana", amount: "1 un (100g)", cal: 89, protein: 1.1, carbs: 23, fat: 0.3 },
          { name: "Canela", amount: "1 pitada", cal: 0, protein: 0, carbs: 0, fat: 0 },
        ],
      },
      {
        id: "vitamina_tarde", label: "Vitamina de Frutas", emoji: "🥤", cal: 180, protein: 8,
        ingredients: [
          { name: "Leite desnatado", amount: "200ml", cal: 70, protein: 7, carbs: 10, fat: 0.5 },
          { name: "Banana", amount: "1 un (100g)", cal: 89, protein: 1.1, carbs: 23, fat: 0.3 },
          { name: "Aveia", amount: "1 colher", cal: 21, protein: 0.7, carbs: 3.5, fat: 0.4 },
        ],
      },
    ],
  },
  dinner: {
    label: "Jantar",
    emoji: "🌙",
    items: [
      // === Leguminosas ===
      { id: "feijao_preto_jt", label: "Feijão Preto", emoji: "🫘", cal: 77, protein: 5, category: "legume", ingredients: [{ name: "Feijão preto cozido", amount: "1 concha (80g)", cal: 77, protein: 5, carbs: 14, fat: 0.5 }] },
      { id: "lentilha_jt", label: "Lentilha", emoji: "🫘", cal: 93, protein: 7, category: "legume", ingredients: [{ name: "Lentilha cozida", amount: "80g", cal: 93, protein: 7, carbs: 16, fat: 0.3 }] },
      { id: "ervilha_jt", label: "Ervilha", emoji: "🫛", cal: 63, protein: 4, category: "legume", ingredients: [{ name: "Ervilha cozida", amount: "80g", cal: 63, protein: 4, carbs: 11, fat: 0.3 }] },
      { id: "grao_de_bico_jt", label: "Grão-de-Bico", emoji: "🫘", cal: 130, protein: 7, category: "legume", ingredients: [{ name: "Grão-de-bico cozido", amount: "80g", cal: 130, protein: 7, carbs: 22, fat: 2 }] },
      // === Carboidratos ===
      { id: "arroz_jt", label: "Arroz", emoji: "🍚", cal: 130, protein: 3, category: "carb", ingredients: [{ name: "Arroz branco cozido", amount: "4 colheres (100g)", cal: 130, protein: 2.5, carbs: 28, fat: 0.3 }] },
      { id: "arroz_feijao_jt", label: "Feijão + Arroz", emoji: "🍚", cal: 207, protein: 8, category: "carb", ingredients: [{ name: "Arroz branco cozido", amount: "4 colheres (100g)", cal: 130, protein: 2.5, carbs: 28, fat: 0.3 }, { name: "Feijão preto cozido", amount: "1 concha (80g)", cal: 77, protein: 5, carbs: 14, fat: 0.5 }] },
      { id: "cuscuz_jt", label: "Cuscuz", emoji: "🌽", cal: 170, protein: 4, category: "carb", ingredients: [{ name: "Cuscuz de milho cozido", amount: "80g", cal: 170, protein: 4, carbs: 36, fat: 0.5 }] },
      { id: "tapioca_jt", label: "Tapioca", emoji: "🫓", cal: 100, protein: 0, category: "carb", ingredients: [{ name: "Goma de tapioca", amount: "3 colheres (30g)", cal: 100, protein: 0.1, carbs: 25, fat: 0 }] },
      { id: "polenta_jt", label: "Polenta Cozida", emoji: "🟡", cal: 70, protein: 2, category: "carb", ingredients: [{ name: "Polenta cozida", amount: "100g", cal: 70, protein: 1.5, carbs: 16, fat: 0.3 }] },
      { id: "macarrao_jt", label: "Macarrão", emoji: "🍝", cal: 160, protein: 5, category: "carb", ingredients: [{ name: "Macarrão cozido", amount: "100g", cal: 160, protein: 5, carbs: 32, fat: 0.8 }] },
      { id: "batata_doce_jt", label: "Batata Doce", emoji: "🍠", cal: 90, protein: 1, category: "carb", ingredients: [{ name: "Batata doce cozida", amount: "100g", cal: 90, protein: 1.3, carbs: 21, fat: 0.1 }] },
      { id: "batata_inglesa_jt", label: "Batata Inglesa", emoji: "🥔", cal: 52, protein: 1, category: "carb", ingredients: [{ name: "Batata inglesa cozida", amount: "100g", cal: 52, protein: 1.2, carbs: 12, fat: 0.1 }] },
      { id: "mandioca_jt", label: "Mandioca", emoji: "🥔", cal: 125, protein: 1, category: "carb", ingredients: [{ name: "Mandioca cozida", amount: "100g", cal: 125, protein: 0.6, carbs: 30, fat: 0.3 }] },
      { id: "inhame_jt", label: "Inhame", emoji: "🥔", cal: 97, protein: 2, category: "carb", ingredients: [{ name: "Inhame cozido", amount: "100g", cal: 97, protein: 2, carbs: 23, fat: 0.1 }] },
      { id: "abobora_jt", label: "Abóbora", emoji: "🎃", cal: 30, protein: 1, category: "carb", ingredients: [{ name: "Abóbora cozida", amount: "100g", cal: 30, protein: 0.8, carbs: 7, fat: 0.1 }] },
      { id: "moranga_jt", label: "Moranga", emoji: "🎃", cal: 28, protein: 1, category: "carb", ingredients: [{ name: "Moranga cozida", amount: "100g", cal: 28, protein: 0.8, carbs: 6, fat: 0.1 }] },
      { id: "milho_jt", label: "Milho", emoji: "🌽", cal: 96, protein: 3, category: "carb", ingredients: [{ name: "Milho cozido", amount: "1 espiga (100g)", cal: 96, protein: 3.2, carbs: 19, fat: 1.2 }] },
      // === Proteínas ===
      { id: "frango_grelhado_jt", label: "Frango Grelhado", emoji: "🍗", cal: 165, protein: 31, category: "protein", ingredients: [{ name: "Peito de frango grelhado", amount: "150g", cal: 165, protein: 31, carbs: 0, fat: 3.6 }] },
      { id: "frango_cozido_jt", label: "Frango Cozido", emoji: "🍗", cal: 150, protein: 28, category: "protein", ingredients: [{ name: "Peito de frango cozido", amount: "150g", cal: 150, protein: 28, carbs: 0, fat: 3.2 }] },
      { id: "carne_grelhada_jt", label: "Carne Grelhada", emoji: "🥩", cal: 180, protein: 32, category: "protein", ingredients: [{ name: "Carne bovina magra grelhada", amount: "120g", cal: 180, protein: 32, carbs: 0, fat: 5.5 }] },
      { id: "carne_cozida_jt", label: "Carne Cozida", emoji: "🥩", cal: 170, protein: 30, category: "protein", ingredients: [{ name: "Carne bovina magra cozida", amount: "120g", cal: 170, protein: 30, carbs: 0, fat: 5 }] },
      { id: "peixe_jt", label: "Peixe", emoji: "🐟", cal: 155, protein: 33, category: "protein", ingredients: [{ name: "Filé de tilápia grelhado", amount: "150g", cal: 155, protein: 33, carbs: 0, fat: 2.3 }] },
      { id: "carne_porco_jt", label: "Carne de Porco", emoji: "🥩", cal: 190, protein: 28, category: "protein", ingredients: [{ name: "Lombo de porco grelhado", amount: "120g", cal: 190, protein: 28, carbs: 0, fat: 8 }] },
      { id: "ovo_jt", label: "Ovo", emoji: "🥚", cal: 78, protein: 6, category: "protein", ingredients: [{ name: "Ovo cozido", amount: "1 un (50g)", cal: 78, protein: 6, carbs: 0.6, fat: 5.3 }] },
      // === Saladas ===
      { id: "salada_jt", label: "Salada de Verduras e Legumes", emoji: "🥗", cal: 64, protein: 1, category: "salad", ingredients: [
        { name: "Alface", amount: "3 folhas (25g)", cal: 2, protein: 0.2, carbs: 0.3, fat: 0 },
        { name: "Tomate", amount: "2 fatias (45g)", cal: 16, protein: 0.3, carbs: 3.5, fat: 0.1 },
        { name: "Pepino", amount: "45g", cal: 5, protein: 0.3, carbs: 1.7, fat: 0.1 },
        { name: "Cenoura", amount: "25g", cal: 8, protein: 0.3, carbs: 2, fat: 0.1 },
        { name: "Azeite", amount: "1 colher chá (5g)", cal: 33, protein: 0, carbs: 0, fat: 3.7 },
      ] },
      { id: "alface_jt", label: "Alface", emoji: "🥬", cal: 5, protein: 0, category: "salad", ingredients: [{ name: "Alface", amount: "3 folhas (30g)", cal: 5, protein: 0.5, carbs: 1, fat: 0 }] },
      { id: "tomate_jt", label: "Tomate", emoji: "🍅", cal: 18, protein: 1, category: "salad", ingredients: [{ name: "Tomate", amount: "1 un (100g)", cal: 18, protein: 0.9, carbs: 4, fat: 0.2 }] },
      { id: "pepino_jt", label: "Pepino", emoji: "🥒", cal: 10, protein: 0, category: "salad", ingredients: [{ name: "Pepino", amount: "80g", cal: 10, protein: 0.4, carbs: 2, fat: 0 }] },
      // === Preparações ===
      { id: "sanduiche_natural_jt", label: "Sanduíche Natural", emoji: "🥪", cal: 270, protein: 22, category: "prep", ingredients: [
        { name: "Pão integral", amount: "2 fatias (50g)", cal: 120, protein: 5, carbs: 22, fat: 1.5 },
        { name: "Peito de frango", amount: "60g", cal: 75, protein: 16, carbs: 0, fat: 1 },
        { name: "Requeijão light", amount: "15g", cal: 24, protein: 1.5, carbs: 0.5, fat: 2 },
        { name: "Alface", amount: "2 folhas", cal: 3, protein: 0.3, carbs: 0.5, fat: 0 },
        { name: "Tomate", amount: "2 fatias", cal: 5, protein: 0.2, carbs: 1, fat: 0 },
      ] },
      { id: "rap10_carne_jt", label: "Rap10 + Carne", emoji: "🌯", cal: 250, protein: 22, category: "prep", ingredients: [
        { name: "Rap10 (tortilha)", amount: "1 un (40g)", cal: 100, protein: 3, carbs: 18, fat: 2 },
        { name: "Carne bovina magra", amount: "80g", cal: 120, protein: 21, carbs: 0, fat: 3.7 },
        { name: "Alface", amount: "1 folha", cal: 2, protein: 0.1, carbs: 0.3, fat: 0 },
        { name: "Tomate", amount: "2 fatias", cal: 5, protein: 0.2, carbs: 1, fat: 0 },
      ] },
      { id: "crepioca_frango_queijo_jt", label: "Crepioca + Frango e Queijo", emoji: "🥚", cal: 260, protein: 24, category: "prep", ingredients: [
        { name: "Ovo", amount: "1 un (50g)", cal: 72, protein: 6, carbs: 0.6, fat: 5 },
        { name: "Goma de tapioca", amount: "2 colheres (20g)", cal: 67, protein: 0.1, carbs: 17, fat: 0 },
        { name: "Peito de frango desfiado", amount: "50g", cal: 63, protein: 13, carbs: 0, fat: 0.8 },
        { name: "Queijo branco", amount: "20g", cal: 47, protein: 4, carbs: 0.3, fat: 3.3 },
      ] },
      { id: "omelete_jt", label: "Omelete", emoji: "🥚", cal: 180, protein: 14, category: "prep", ingredients: [
        { name: "Ovo", amount: "2 un (100g)", cal: 143, protein: 12, carbs: 1.1, fat: 10 },
        { name: "Tomate", amount: "30g", cal: 5, protein: 0.3, carbs: 1, fat: 0 },
        { name: "Cebola", amount: "15g", cal: 6, protein: 0.2, carbs: 1.4, fat: 0 },
        { name: "Sal", amount: "1 pitada", cal: 0, protein: 0, carbs: 0, fat: 0 },
      ] },
    ],
  },
  optional: {
    label: "Lanches Opcionais",
    emoji: "🍿",
    items: [
      {
        id: "chocolate_leite_op", label: "Chocolate ao Leite", emoji: "🍫", cal: 135, protein: 2,
        ingredients: [
          { name: "Chocolate ao leite", amount: "25g", cal: 135, protein: 2, carbs: 15, fat: 8 },
        ],
      },
      {
        id: "chocolate_70_op", label: "Chocolate 70%", emoji: "🍫", cal: 120, protein: 2,
        ingredients: [
          { name: "Chocolate 70% cacau", amount: "20g", cal: 120, protein: 1.5, carbs: 8, fat: 9 },
        ],
      },
      {
        id: "chocolate_branco_op", label: "Chocolate Branco", emoji: "🍫", cal: 140, protein: 1.5,
        ingredients: [
          { name: "Chocolate branco", amount: "25g", cal: 140, protein: 1.5, carbs: 15, fat: 8.5 },
        ],
      },
      {
        id: "bombom_op", label: "Bombom Comum", emoji: "🍬", cal: 90, protein: 1,
        ingredients: [
          { name: "Bombom", amount: "1 unidade (20g)", cal: 90, protein: 1, carbs: 11, fat: 5 },
        ],
      },
      {
        id: "nutella_op", label: "Nutella", emoji: "🥜", cal: 100, protein: 1,
        ingredients: [
          { name: "Nutella", amount: "1 colher sopa (18g)", cal: 100, protein: 1, carbs: 11, fat: 6 },
        ],
      },
      {
        id: "doce_leite_op", label: "Doce de Leite", emoji: "🍮", cal: 65, protein: 1.5,
        ingredients: [
          { name: "Doce de leite", amount: "1 colher sopa (20g)", cal: 65, protein: 1.5, carbs: 11, fat: 2 },
        ],
      },
      {
        id: "brigadeiro_op", label: "Brigadeiro", emoji: "🍫", cal: 80, protein: 1,
        ingredients: [
          { name: "Brigadeiro", amount: "1 unidade (25g)", cal: 80, protein: 1, carbs: 12, fat: 3 },
        ],
      },
      {
        id: "pacoca_op", label: "Paçoca", emoji: "🥜", cal: 100, protein: 3,
        ingredients: [
          { name: "Paçoca", amount: "1 unidade (22g)", cal: 100, protein: 3, carbs: 10, fat: 6 },
        ],
      },
      {
        id: "leite_condensado_op", label: "Leite Condensado", emoji: "🥛", cal: 65, protein: 1.5,
        ingredients: [
          { name: "Leite condensado", amount: "1 colher sopa (20g)", cal: 65, protein: 1.5, carbs: 11, fat: 2 },
        ],
      },
    ],
  },
};

export const mealOrder: MealKey[] = ["breakfast", "morning_snack", "lunch", "snack", "dinner", "optional"];

/** Map meal type key used in diary to the corresponding recipe meal keys */
export const diaryMealToRecipeKey: Record<string, MealKey[]> = {
  breakfast: ["breakfast"],
  morning_snack: ["morning_snack"],
  lunch: ["lunch"],
  snack: ["snack", "optional"],
  afternoon_snack: ["snack"],
  dinner: ["dinner"],
  optional_snack: ["optional"],
  other: ["breakfast", "morning_snack", "lunch", "snack", "dinner", "optional"],
};

/** Parse recipe IDs encoded as `${baseId}__scale__${factor}` and tolerate `:free` suffixes */
export function parseScaledRecipeId(id: string): { baseId: string; scale: number } {
  const normalizedId = id.replace(/:free$/, "");
  const marker = "__scale__";
  const idx = normalizedId.lastIndexOf(marker);
  if (idx === -1) return { baseId: normalizedId, scale: 1 };

  const baseId = normalizedId.slice(0, idx);
  const rawScale = Number(normalizedId.slice(idx + marker.length));
  if (!Number.isFinite(rawScale) || rawScale <= 0) {
    return { baseId: normalizedId, scale: 1 };
  }

  return { baseId, scale: rawScale };
}

/** Build an encoded scaled recipe ID */
export function buildScaledRecipeId(baseId: string, scale: number): string {
  if (!Number.isFinite(scale) || scale <= 0) return baseId;
  if (Math.abs(scale - 1) < 0.0001) return baseId;
  return `${baseId}__scale__${scale.toFixed(6)}`;
}

/**
 * Minimum whole/half units by ingredient type.
 * Eggs in composed recipes (like pancakes) → min 1 un
 * Hard-boiled eggs (standalone) → min 0.5 un
 * Fruits (banana, maçã, laranja) → min 1 un
 * Pão → min 0.5 un
 * Default → min 0.5 un
 */
/** Minimum units and rounding step by ingredient type */
function getUnitConfig(ingredientName: string): { min: number; step: number } {
  const lower = ingredientName.toLowerCase();
  // Fruits that should only be whole units
  if (lower.includes("laranja") ||
      lower.includes("kiwi") || lower.includes("caqui") || lower.includes("pêra") ||
      lower.includes("bergamota") || lower.includes("tangerina")) {
    return { min: 1, step: 1 }; // integer only
  }
  // Fruits that accept halves
  if (lower.includes("banana") || lower.includes("maçã") || lower.includes("mamão") || lower.includes("manga") ||
      lower.includes("abacate") || lower.includes("melão") || lower.includes("melancia")) {
    return { min: 0.5, step: 0.5 };
  }
  if (lower === "ovo cozido" || lower === "ovo" || lower.includes("ovo")) return { min: 1, step: 1 };
  if (lower.includes("pão")) return { min: 0.5, step: 0.5 };
  return { min: 0.5, step: 0.25 };
}

function getDiscreteAdjustedScale(amount: string, scale: number, ingredientName: string = ""): number {
  // "un" units (eggs, fruits, bread rolls) — snap to whole/half steps
  const unitMatch = amount.match(/(\d+(?:\.\d+)?)\s*un\b/i);
  if (unitMatch) {
    const originalUnits = Number(unitMatch[1]);
    if (!Number.isFinite(originalUnits) || originalUnits <= 0) return scale;
    const scaledUnits = originalUnits * scale;
    const { min, step } = getUnitConfig(ingredientName);
    const roundedUnits = Math.max(min, Math.round(scaledUnits / step) * step);
    return roundedUnits / originalUnits;
  }

  // "fatia" units for Pão de forma — snap to whole slices (≈25g each)
  // so the displayed slice count, grams and kcal all stay in sync.
  if (/pão\s+de\s+forma/i.test(ingredientName)) {
    const sliceMatch = amount.match(/(\d+(?:\.\d+)?)\s*fatia/i);
    if (sliceMatch) {
      const originalSlices = Number(sliceMatch[1]);
      if (!Number.isFinite(originalSlices) || originalSlices <= 0) return scale;
      const scaledSlices = originalSlices * scale;
      const roundedSlices = Math.max(1, Math.round(scaledSlices));
      return roundedSlices / originalSlices;
    }
  }

  return scale;
}

// Descriptive unit definitions: [regex-match, singular, plural, minimum]
const DESCRIPTIVE_UNITS: Array<{ pattern: RegExp; singular: string; plural: string; min: number }> = [
  { pattern: /fatias?/i, singular: "fatia", plural: "fatias", min: 1 },
  { pattern: /folhas?/i, singular: "folha", plural: "folhas", min: 1 },
  { pattern: /colheres?/i, singular: "colher", plural: "colheres", min: 1 },
  { pattern: /x[ií]caras?/i, singular: "xícara", plural: "xícaras", min: 1 },
  { pattern: /copos?/i, singular: "copo", plural: "copos", min: 1 },
  { pattern: /peda[çc]os?/i, singular: "pedaço", plural: "pedaços", min: 1 },
  { pattern: /unidades?/i, singular: "unidade", plural: "unidades", min: 1 },
  { pattern: /dentes?/i, singular: "dente", plural: "dentes", min: 1 },
  { pattern: /scoops?/i, singular: "scoop", plural: "scoops", min: 1 },
];

// Units that should NOT be scaled (seasoning, pinches, etc.)
const FIXED_UNITS = /pitadas?|a gosto|punhado/i;

/** Round grams/ml to "nice" multiples: ≤5→round, ≤25→nearest 5, >25→nearest 10 */
function roundToNice(val: number): number {
  if (val <= 0) return 0;
  if (val <= 5) return Math.round(val);
  if (val <= 25) return Math.round(val / 5) * 5;
  return Math.round(val / 10) * 10;
}

function scaleAmountText(amount: string, scale: number, ingredientName: string = ""): string {
  let result = amount;

  // 1. Scale descriptive units (e.g. "2 fatias", "3 folhas", "1 colher")
  // but NOT units inside parentheses like "(50g)" — those are handled by g/ml scaling
  for (const unit of DESCRIPTIVE_UNITS) {
    const re = new RegExp(`(\\d+(?:[.,]\\d+)?)\\s*(${unit.pattern.source})`, "gi");
    result = result.replace(re, (match, numStr, unitStr) => {
      // Don't scale if it's a fixed/seasoning unit
      if (FIXED_UNITS.test(unitStr)) return match;
      const n = Number(numStr.replace(",", "."));
      const scaled = Math.max(unit.min, Math.round(n * scale));
      const word = scaled === 1 ? unit.singular : unit.plural;
      return `${scaled} ${word}`;
    });
  }

  // 2. Scale explicit grams/ml occurrences, rounding to nice multiples of 5
  result = result
    .replace(/(\d+(?:\.\d+)?)\s*g\b/gi, (_, n) => `${roundToNice(Number(n) * scale)}g`)
    .replace(/(\d+(?:\.\d+)?)\s*ml\b/gi, (_, n) => `${roundToNice(Number(n) * scale)}ml`)
    .replace(/(\d+(?:\.\d+)?)\s*un\b/gi, (_, n) => {
      // Use getUnitConfig for proper rounding (eggs→whole, bread→halves, etc.)
      const config = getUnitConfig(ingredientName);
      const rawUnits = Number(n) * scale;
      const rounded = Math.max(config.min, Math.round(rawUnits / config.step) * config.step);
      const text = Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(2).replace(/\.00$/, "").replace(/0$/, "");
      return `${text} un`;
    });

  return result;
}

/** Minimum practical grams per ingredient type to avoid symbolic portions */
function getIngredientFloorGrams(ingredientName: string): number {
  const lower = ingredientName.toLowerCase();
  // Protein sources: min 30g
  if (lower.includes("ovo") || lower.includes("frango") || lower.includes("carne") ||
      lower.includes("peixe") || lower.includes("tilápia") || lower.includes("salmão") ||
      lower.includes("sardinha") || lower.includes("peru") || lower.includes("lombo")) {
    return 30;
  }
  // Fats & energy-dense condiments: min 8g (≈1 colher de chá cheia)
  if (lower.includes("pasta de amendoim") || lower.includes("amendoim")) return 8;
  if (lower.includes("azeite")) return 3;
  if (lower.includes("manteiga") || lower.includes("margarina")) return 5;
  if (lower.includes("requeijão") || lower.includes("cream cheese")) return 10;
  if (lower.includes("maionese")) return 5;
  if (lower.includes("mel")) return 5;
  if (lower.includes("doce de leite") || lower.includes("leite condensado")) return 10;
  if (lower.includes("chocolate") || lower.includes("brigadeiro") || lower.includes("paçoca")) return 10;
  if (lower.includes("granola")) return 10;
  // Goma de tapioca: min 15g (≈1.5 colheres, minimum practical portion)
  if (lower.includes("goma de tapioca") || lower.includes("tapioca")) return 15;
  // Cuscuz de milho: min 50g (porção mínima realista cozida)
  if (lower.includes("cuscuz")) return 50;
  // Vegetables/produce: min 20g (minimum practical slice/portion)
  if (lower.includes("tomate") || lower.includes("cebola") || lower.includes("cenoura") ||
      lower.includes("pepino") || lower.includes("alface") || lower.includes("rúcula") ||
      lower.includes("espinafre") || lower.includes("repolho") || lower.includes("brócolis") ||
      lower.includes("couve") || lower.includes("abóbora")) {
    return 20;
  }
  // Fruits: min 30g
  if (lower.includes("banana") || lower.includes("maçã") || lower.includes("laranja") ||
      lower.includes("morango") || lower.includes("mamão") || lower.includes("melão") ||
      lower.includes("melancia") || lower.includes("abacaxi")) {
    return 30;
  }
  // Dairy: min 30g
  if (lower.includes("iogurte") || lower.includes("leite") || lower.includes("queijo")) {
    return 30;
  }
  return 0; // no floor for other items
}

/**
 * Maximum practical grams per ingredient. Used in `applyScaleToRecipe` to clamp
 * individual ingredients that would otherwise be inflated by the meal solver.
 * Returns 0 when there is no per-ingredient ceiling (recipe-level limits apply).
 */
function getIngredientCeilingGrams(
  ingredientName: string,
  ingredientCount: number,
  siblingNames: string[] = [],
): number {
  const lower = ingredientName.toLowerCase();
  // Fluid milk: hard cap 200ml ONLY in composite recipes that contain fruit
  // (vitaminas, mingaus). In standalone "Leite Desnatado" or "Café com Leite",
  // milk IS the meal and the recipe-level macroSolver limits are enough.
  if (lower.includes("leite") && !lower.includes("condensado") && !lower.includes("doce de leite")) {
    const hasFruitSibling = siblingNames.some((n) =>
      /banana|maçã|maca|morango|mam[ãa]o|abacaxi|manga|kiwi|melão|melao|melancia|fruta|uva|laranja/i.test(n),
    );
    return hasFruitSibling ? 200 : 0;
  }
  // Composite-only ceilings: when bread (pão de forma) scales 1→2 slices,
  // the egg/cheese/spread should NOT also double — they stay at their natural
  // serving so only the bread grows. Skip for single-ingredient (standalone)
  // recipes, where the egg/cheese IS the meal.
  if (ingredientCount >= 2) {
    if (lower.includes("requeijão") || lower.includes("requeijao")) return 30;
    if (lower.includes("queijo")) return 60;
    if (lower.includes("ovo")) return 120; // ~2 eggs maximum in a sandwich
  }
  return 0;
}

/**
 * Returns true when scaling the recipe would trigger discrete unit snapping
 * (whole eggs, whole fruits, bread slices, etc.). Recipes that fail this check
 * are "continuous" — their kcal moves smoothly with scale, so they can absorb
 * post-snap calorie residuals from sibling recipes inside the same meal option.
 *
 * Reuses the exact criteria applied by `getDiscreteAdjustedScale` to avoid
 * classification drift.
 */
export function recipeHasDiscreteSnap(recipe: Recipe): boolean {
  return recipe.ingredients.some((ing) => {
    if (/(\d+(?:\.\d+)?)\s*un\b/i.test(ing.amount)) return true;
    if (/pão\s+de\s+forma/i.test(ing.name) && /fatia/i.test(ing.amount)) return true;
    return false;
  });
}

/**
 * Returns true when the recipe contains at least one ingredient that scales
 * smoothly (no discrete snap and no presentation floor that would freeze it).
 * Used by the post-snap compensator to treat mixed recipes (e.g. vitamina =
 * banana 1un + leite + aveia) as "continuous absorbers": even though the
 * banana snaps, the leite/aveia can grow proportionally to close kcal gaps.
 *
 * Continuous absorbers: leite (líquido), iogurte líquido, whey, azeite, mel,
 * pasta de amendoim, aveia, granola — anything measured in g/ml without
 * discrete units. The ingredient-level ceilings inside `applyScaleToRecipe`
 * still cap each absorber at its realistic maximum.
 */
export function recipeHasContinuousAbsorber(recipe: Recipe): boolean {
  return recipe.ingredients.some((ing) => {
    const name = ing.name.toLowerCase();
    const amount = ing.amount.toLowerCase();
    // Skip if this ingredient itself snaps to discrete units.
    if (/(\d+(?:\.\d+)?)\s*un\b/i.test(amount)) return false;
    if (/pão\s+de\s+forma/i.test(name) && /fatia/i.test(amount)) return false;
    // Liquid/granular absorbers measured in g/ml.
    if (
      name.includes("leite") ||
      name.includes("iogurte") ||
      name.includes("whey") ||
      name.includes("aveia") ||
      name.includes("granola") ||
      name.includes("azeite") ||
      name.includes("mel") ||
      name.includes("pasta de amendoim") ||
      // Staple carbs measured in grams — scale smoothly without discrete snap,
      // so they can absorb residual kcal gaps in composite snacks/meals.
      name.includes("cuscuz") ||
      name.includes("arroz") ||
      name.includes("tapioca") ||
      name.includes("mandioca") ||
      name.includes("macarrão") ||
      name.includes("macarrao") ||
      name.includes("batata")
    ) {
      return true;
    }
    return false;
  });
}

function applyScaleToRecipe(recipe: Recipe, scale: number): Recipe {
  if (Math.abs(scale - 1) < 0.0001) return recipe;

  const scaledIngredients = recipe.ingredients.map((ing) => {
    let adjustedScale = getDiscreteAdjustedScale(ing.amount, scale, ing.name);

    // Enforce practical ingredient floor: if scaled grams would be below minimum,
    // clamp the scale up for this ingredient
    const floorGrams = getIngredientFloorGrams(ing.name);
    if (floorGrams > 0) {
      const baseGrams = ing.quantity_grams || extractGrams(ing.amount);
      if (baseGrams && baseGrams > 0) {
        const scaledGrams = baseGrams * adjustedScale;
        if (scaledGrams < floorGrams) {
          adjustedScale = floorGrams / baseGrams;
        }
      }
    }

    // Enforce practical ingredient ceiling: if scaled grams would exceed the
    // realistic maximum (e.g. 200ml of milk in a vitamina), clamp scale down.
    const siblingNames = recipe.ingredients.filter((s) => s !== ing).map((s) => s.name);
    const ceilingGrams = getIngredientCeilingGrams(ing.name, recipe.ingredients.length, siblingNames);
    if (ceilingGrams > 0) {
      const baseGrams = ing.quantity_grams || extractGrams(ing.amount);
      if (baseGrams && baseGrams > 0) {
        const scaledGrams = baseGrams * adjustedScale;
        if (scaledGrams > ceilingGrams) {
          adjustedScale = ceilingGrams / baseGrams;
        }
      }
    }

    return {
      ...ing,
      amount: scaleAmountText(ing.amount, adjustedScale, ing.name),
      quantity_grams: ing.quantity_grams ? roundToNice(ing.quantity_grams * adjustedScale) : ing.quantity_grams,
      cal: Math.round(ing.cal * adjustedScale),
      protein: Math.round(ing.protein * adjustedScale * 10) / 10,
      carbs: Math.round(ing.carbs * adjustedScale * 10) / 10,
      fat: Math.round(ing.fat * adjustedScale * 10) / 10,
    };
  });

  // Couple couve↔maçã in green-juice recipes: both ingredients must scale
  // together in 1:1 integer steps (1 maçã + 1 folha, 2 maçãs + 2 folhas, ...).
  // No half apples or mismatched leaves — the user wants identical whole counts.
  const appleIng = scaledIngredients.find((i) => /maçã|maca/i.test(i.name));
  const kaleIng = scaledIngredients.find((i) => /couve/i.test(i.name));
  if (appleIng && kaleIng) {
    const appleUnitMatch = appleIng.amount.match(/(\d+(?:\.\d+)?)\s*un\b/i);
    const kaleLeafMatch = kaleIng.amount.match(/(\d+(?:[.,]\d+)?)\s*folhas?/i);
    if (appleUnitMatch && kaleLeafMatch) {
      const apples = Number(appleUnitMatch[1]);
      const currentLeaves = Number(kaleLeafMatch[1].replace(",", "."));
      // Lock both to the same integer count (≥1). Use the larger of the two
      // current values so we never undersize the juice after rounding.
      const target = Math.max(
        1,
        Math.round(Math.max(Number.isFinite(apples) ? apples : 1, Number.isFinite(currentLeaves) ? currentLeaves : 1)),
      );

      // Resync apple to integer units
      if (Number.isFinite(apples) && apples > 0 && target !== apples) {
        const appleRatio = target / apples;
        // Rebuild "X un (Yg)" preserving the parenthetical grams scaling
        const gramsMatch = appleIng.amount.match(/\(([^)]+)\)/);
        let suffix = "";
        if (gramsMatch) {
          suffix = ` (${gramsMatch[1].replace(/(\d+(?:\.\d+)?)\s*g/i, (_, n) => `${roundToNice(Number(n) * appleRatio)}g`)})`;
        }
        appleIng.amount = `${target} un${suffix}`;
        appleIng.cal = Math.round(appleIng.cal * appleRatio);
        appleIng.protein = Math.round(appleIng.protein * appleRatio * 10) / 10;
        appleIng.carbs = Math.round(appleIng.carbs * appleRatio * 10) / 10;
        appleIng.fat = Math.round(appleIng.fat * appleRatio * 10) / 10;
        if (appleIng.quantity_grams) {
          appleIng.quantity_grams = roundToNice(appleIng.quantity_grams * appleRatio);
        }
      }

      // Resync kale leaves to match
      if (Number.isFinite(currentLeaves) && currentLeaves > 0 && target !== currentLeaves) {
        const kaleRatio = target / currentLeaves;
        const word = target === 1 ? "folha" : "folhas";
        kaleIng.amount = `${target} ${word}`;
        kaleIng.cal = Math.round(kaleIng.cal * kaleRatio);
        kaleIng.protein = Math.round(kaleIng.protein * kaleRatio * 10) / 10;
        kaleIng.carbs = Math.round(kaleIng.carbs * kaleRatio * 10) / 10;
        kaleIng.fat = Math.round(kaleIng.fat * kaleRatio * 10) / 10;
        if (kaleIng.quantity_grams) {
          kaleIng.quantity_grams = roundToNice(kaleIng.quantity_grams * kaleRatio);
        }
      }
    }
  }

  // Couple aveia ↔ banana: limit aveia to 10g per 50g de banana (ratio 1:5).
  // Sem isso, em receitas tipo "Aveia + Banana", a aveia escala demais e
  // estoura calorias/macros do snack quando o solver abre o orçamento.
  const bananaIng = scaledIngredients.find((i) => /banana/i.test(i.name));
  const aveiaIng = scaledIngredients.find((i) => /aveia/i.test(i.name));
  if (bananaIng && aveiaIng) {
    const bananaGrams = bananaIng.quantity_grams || extractGrams(bananaIng.amount) || 0;
    const aveiaGrams = aveiaIng.quantity_grams || extractGrams(aveiaIng.amount) || 0;
    const maxAveia = bananaGrams * 0.2; // 10g aveia : 50g banana
    if (bananaGrams > 0 && aveiaGrams > maxAveia && aveiaGrams > 0) {
      const ratio = maxAveia / aveiaGrams;
      aveiaIng.amount = scaleAmountText(aveiaIng.amount, ratio, aveiaIng.name);
      if (aveiaIng.quantity_grams) {
        aveiaIng.quantity_grams = roundToNice(aveiaIng.quantity_grams * ratio);
      }
      aveiaIng.cal = Math.round(aveiaIng.cal * ratio);
      aveiaIng.protein = Math.round(aveiaIng.protein * ratio * 10) / 10;
      aveiaIng.carbs = Math.round(aveiaIng.carbs * ratio * 10) / 10;
      aveiaIng.fat = Math.round(aveiaIng.fat * ratio * 10) / 10;
    }
  }

  const totalCal = scaledIngredients.reduce((sum, i) => sum + i.cal, 0);
  const totalProtein = Math.round(scaledIngredients.reduce((sum, i) => sum + i.protein, 0) * 10) / 10;

  return {
    ...recipe,
    id: buildScaledRecipeId(recipe.id, scale),
    cal: totalCal,
    protein: totalProtein,
    ingredients: scaledIngredients,
  };
}

/** Find a recipe by id across all meal blocks (supports scaled IDs) */
export const findRecipeById = (id: string, sourceRecipes: Record<MealKey, MealBlock> = recipes): Recipe | undefined => {
  const { baseId, scale } = parseScaledRecipeId(id);

  for (const key of mealOrder) {
    const found = sourceRecipes[key].items.find(r => r.id === baseId);
    if (found) {
      return scale === 1 ? found : applyScaleToRecipe(found, scale);
    }
  }

  return undefined;
};
